package com.evoapps.evoshift.ui.tips

import com.evoapps.evoshift.widgets.LocalOnColours
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import com.evoapps.evoshift.ui.theme.animateItemIfMotion
import com.evoapps.evoshift.util.usesDecimalComma
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.Download
import androidx.compose.material.icons.filled.Info
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.domain.model.MyTipShare
import com.evoapps.evoshift.domain.model.TipPeriod
import com.evoapps.evoshift.domain.model.TipPool
import com.evoapps.evoshift.ui.Cream
import com.evoapps.evoshift.ui.DarkGold
import com.evoapps.evoshift.ui.DeepNavy
import com.evoapps.evoshift.ui.adaptive.FormMaxWidth
import com.evoapps.evoshift.ui.adaptive.contentWidth
import com.evoapps.evoshift.ui.finance.formatDecimal
import com.evoapps.evoshift.ui.finance.formatMoney
import com.evoapps.evoshift.ui.util.asString
import com.evoapps.evoshift.ui.util.dayAndDate
import com.evoapps.evoshift.ui.util.dayAndMonth
import com.evoapps.evoshift.util.appLanguage
import com.evoapps.evoshift.viewmodels.TipsViewModel
import com.evoapps.evoshift.widgets.EvoTopBar
import com.evoapps.evoshift.widgets.LoadingOverlay
import com.evoapps.evoshift.widgets.RegularCard
import com.evoapps.evoshift.widgets.StandoutCard
import com.evoapps.evoshift.widgets.TopBarAction
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.tips_export
import evoshift.composeapp.generated.resources.tips_from_tablet
import evoshift.composeapp.generated.resources.tips_hours
import evoshift.composeapp.generated.resources.tips_my_empty
import evoshift.composeapp.generated.resources.tips_my_title
import evoshift.composeapp.generated.resources.tips_new
import evoshift.composeapp.generated.resources.tips_people
import evoshift.composeapp.generated.resources.tips_period_range
import evoshift.composeapp.generated.resources.tips_pools_empty
import evoshift.composeapp.generated.resources.tips_pools_title
import evoshift.composeapp.generated.resources.tips_tax_note
import evoshift.composeapp.generated.resources.tips_title
import evoshift.composeapp.generated.resources.tips_waiting_for_hours
import evoshift.composeapp.generated.resources.tips_your_share
import kotlinx.datetime.LocalDate
import org.jetbrains.compose.resources.stringResource

/**
 * SH-124: Tips (More → Tips). The note that tax is the employer's and accountant's, then the
 * person's own shares ("Your share: 85,00 zł") and, for managers, the workplace's pools with
 * everyone's share: "New tip pool" in the top bar, a pool's card opens it to change or remove
 * ([TipPoolEditor]), its "Export CSV" saves the split.
 */
@Composable
fun TipsScreen(viewModel: TipsViewModel, onNavigateBack: (() -> Unit)?) {
    val state by viewModel.uiState.collectAsStateWithLifecycle()
    val snackbarHostState = remember { SnackbarHostState() }
    val messageText = state.message?.asString()
    LaunchedEffect(messageText) {
        messageText?.let {
            snackbarHostState.showSnackbar(it)
            viewModel.messageShown()
        }
    }

    Scaffold(
        containerColor = Cream,
        snackbarHost = { SnackbarHost(snackbarHostState) },
        topBar = {
            EvoTopBar(
                title = stringResource(Res.string.tips_title),
                onBack = onNavigateBack,
                showWorkplace = true,
                actions = buildList {
                    if (state.canEdit) add(TopBarAction(stringResource(Res.string.tips_new), Icons.Default.Add, { viewModel.openNew() }, collapsible = false))
                }
            )
        }
    ) { padding ->
        Box(Modifier.fillMaxSize().contentWidth(FormMaxWidth).padding(padding)) {
            LazyColumn(
                modifier = Modifier.fillMaxSize(),
                contentPadding = PaddingValues(16.dp),
                verticalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                item("note") { TaxNote() }
                item("mine") { SectionTitle(stringResource(Res.string.tips_my_title)) }
                if (state.myShares.isEmpty() && !state.isLoading) {
                    item("mine-empty") { Muted(stringResource(Res.string.tips_my_empty)) }
                }
                items(state.myShares, key = { "mine-${it.poolId}" }) { Box(Modifier.animateItemIfMotion(this), propagateMinConstraints = true) { MyShareCard(it) } }
                if (state.seesPools) {
                    item("pools") { SectionTitle(stringResource(Res.string.tips_pools_title)) }
                    if (state.pools.isEmpty() && !state.isLoading) {
                        item("pools-empty") { Muted(stringResource(Res.string.tips_pools_empty)) }
                    }
                    items(state.pools, key = { "pool-${it.id}" }) { pool ->
                        Box(Modifier.animateItemIfMotion(this), propagateMinConstraints = true) {
                            PoolCard(
                                pool = pool,
                                onOpen = if (state.canEdit) ({ viewModel.openPool(pool) }) else null,
                                onExport = { viewModel.export(pool) }
                            )
                        }
                    }
                }
            }
            LoadingOverlay(isLoading = state.isLoading && state.myShares.isEmpty() && state.pools.isEmpty())
        }
    }

    state.editor?.let { editor ->
        TipPoolEditor(
            editor = editor,
            isSaving = state.isSaving,
            onPeriod = viewModel::setPeriod,
            onMove = viewModel::movePeriod,
            onAmount = viewModel::setAmount,
            onWeight = viewModel::setWeight,
            onSave = viewModel::save,
            onDelete = editor.existing?.let { pool -> { viewModel.delete(pool) } },
            onDismiss = viewModel::closeEditor
        )
    }
}

/** Not pay: tax and reporting of tips are the employer's and their accountant's. */
@Composable
fun TaxNote() {
    RegularCard(Modifier.fillMaxWidth()) {
        Row(Modifier.padding(16.dp), verticalAlignment = Alignment.Top) {
            Icon(Icons.Default.Info, contentDescription = null, tint = DarkGold)
            Spacer(Modifier.width(12.dp))
            Text(stringResource(Res.string.tips_tax_note), style = MaterialTheme.typography.bodyMedium, color = DeepNavy)
        }
    }
}

@Composable
private fun SectionTitle(text: String) {
    Text(
        text.uppercase(),
        style = MaterialTheme.typography.labelMedium,
        fontWeight = FontWeight.Bold,
        color = DeepNavy.copy(alpha = 0.6f),
        modifier = Modifier.padding(start = 8.dp, top = 8.dp)
    )
}

@Composable
private fun Muted(text: String) {
    Text(text, color = DeepNavy.copy(alpha = 0.6f), style = MaterialTheme.typography.bodyMedium, modifier = Modifier.padding(horizontal = 8.dp))
}

@Composable
private fun MyShareCard(share: MyTipShare) {
    StandoutCard(Modifier.fillMaxWidth()) {
        Column(Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(4.dp)) {
            Text(periodLabel(share.period, share.startDate, share.endDate), style = MaterialTheme.typography.bodyMedium)
            Text(
                stringResource(Res.string.tips_your_share, money(share.amountMinor, share.currency)),
                style = MaterialTheme.typography.titleLarge,
                fontWeight = FontWeight.Bold
            )
            Text(stringResource(Res.string.tips_hours, hoursText(share.minutesWorked)), style = MaterialTheme.typography.bodySmall)
        }
    }
}

@Composable
private fun PoolCard(pool: TipPool, onOpen: (() -> Unit)?, onExport: () -> Unit) {
    RegularCard(Modifier.fillMaxWidth(), onClick = onOpen) {
        Column(Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
            Text(periodLabel(pool.period, pool.startDate, pool.endDate), style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold, color = DeepNavy)
            Text(money(pool.amountMinor, pool.currency), style = MaterialTheme.typography.titleSmall, fontWeight = FontWeight.Bold, color = DeepNavy)
            if (pool.source == "KIOSK") Muted(stringResource(Res.string.tips_from_tablet))
            if (pool.waitingForHours) {
                Muted(stringResource(Res.string.tips_waiting_for_hours))
            } else {
                Muted(stringResource(Res.string.tips_people, pool.shares.size))
                ShareRows(pool.shares.map { Triple(it.displayName, it.minutesWorked, it.amountMinor) }, pool.currency)
                TextButton(onClick = onExport, colors = ButtonDefaults.textButtonColors(contentColor = DarkGold)) {
                    Icon(Icons.Default.Download, contentDescription = null, modifier = Modifier.padding(end = 4.dp))
                    Text(stringResource(Res.string.tips_export))
                }
            }
        }
    }
}

/** Name, hours and share per person. */
@Composable
fun ShareRows(rows: List<Triple<String, Long, Long>>, currency: String) {
    val on = LocalOnColours.current
    Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
        rows.forEach { (name, minutes, amount) ->
            Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
                Text(name, modifier = Modifier.weight(1f), color = on.onSurface, style = MaterialTheme.typography.bodyMedium)
                Text(stringResource(Res.string.tips_hours, hoursText(minutes)), color = on.onSurfaceVariant, style = MaterialTheme.typography.bodySmall)
                Spacer(Modifier.width(12.dp))
                Text(money(amount, currency), color = on.onSurface, fontWeight = FontWeight.Bold, style = MaterialTheme.typography.bodyMedium)
            }
        }
    }
}

/** "Tuesday, 3 March", or "2 March – 8 March" for a week. */
@Composable
fun periodLabel(period: TipPeriod, startDate: String, endDate: String): String {
    val start = runCatching { LocalDate.parse(startDate) }.getOrNull() ?: return startDate
    val end = runCatching { LocalDate.parse(endDate) }.getOrNull() ?: start
    return if (period == TipPeriod.DAY) dayAndDate(start) else stringResource(Res.string.tips_period_range, dayAndMonth(start), dayAndMonth(end))
}

/** grosze as money: "85,00 zł". */
fun money(amountMinor: Long, currency: String): String = formatMoney(amountMinor / 100.0, currency)

/** Minutes as hours with one decimal in the app's language ("7,8" / "7.8"). */
fun hoursText(minutes: Long): String {
    val polish = usesDecimalComma(appLanguage())
    return formatDecimal(minutes / 60.0, 1, groupSeparator = if (polish) ' ' else ',', decimalSeparator = if (polish) ',' else '.')
}
